import { Mic, MicOff, Volume2, VolumeX, Crown } from 'lucide-react';
import clsx from 'clsx';
import { getInitials } from '@/utils/formatters';

export interface Participant {
  id: string;
  name: string;
  isMuted: boolean;
  isDeafened: boolean;
  isSpeaking: boolean;
  isHost: boolean;
  avatar?: string;
}

interface VoiceParticipantProps {
  participant: Participant;
  isCurrentUser?: boolean;
}

// getAvatarColor is different from formatters.ts (uses gradients instead of solid colors)
function getAvatarColor(id: string): string {
  const colors = [
  'from-accent-violet to-accent-blue',
  'from-accent-emerald to-accent-cyan',
  'from-accent-amber to-accent-rose',
  'from-accent-blue to-accent-violet',
  'from-accent-rose to-accent-amber',
  'from-accent-cyan to-accent-emerald',
  ];
  const index = id.charCodeAt(0) % colors.length;
  return colors[index];
}

export function VoiceParticipant({ participant, isCurrentUser }: VoiceParticipantProps) {
  return (
  <div
      className={clsx(
    'flex items-center gap-3 p-3 rounded-lg transition-all',
    participant.isSpeaking
          ? 'bg-accent-emerald/10 ring-2 ring-accent-emerald'
          : 'bg-surface-secondary hover:bg-surface-tertiary',
    isCurrentUser && 'border border-accent-violet/30'
      )}
  >
      {/* Avatar */}
      <div className="relative">
    <div
          className={clsx(
      'w-10 h-10 rounded-full bg-gradient-to-br flex items-center justify-center',
      getAvatarColor(participant.id),
      participant.isSpeaking && 'ring-2 ring-accent-emerald ring-offset-2 ring-offset-surface-primary'
          )}
    >
          <span className="text-sm font-semibold text-white">
      {getInitials(participant.name)}
          </span>
    </div>

    {/* Speaking indicator */}
    {participant.isSpeaking && (
          <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-accent-emerald flex items-center justify-center">
      <Volume2 className="w-2.5 h-2.5 text-white" />
          </div>
    )}
      </div>

      {/* Name and status */}
      <div className="flex-1 min-w-0">
    <div className="flex items-center gap-2">
          <span className={clsx(
      'text-sm font-medium truncate',
      isCurrentUser ? 'text-accent-violet' : 'text-text-primary'
          )}>
      {participant.name}
      {isCurrentUser && ' (tu)'}
          </span>
          {participant.isHost && (
      <Crown className="w-3.5 h-3.5 text-accent-amber shrink-0" />
          )}
    </div>
    <div className="flex items-center gap-2 mt-0.5">
          {participant.isMuted && (
      <span className="text-xs text-accent-rose flex items-center gap-1">
              <MicOff className="w-3 h-3" />
              Muto
      </span>
          )}
          {participant.isDeafened && (
      <span className="text-xs text-text-quaternary flex items-center gap-1">
              <VolumeX className="w-3 h-3" />
              Silenziato
      </span>
          )}
          {!participant.isMuted && !participant.isDeafened && participant.isSpeaking && (
      <span className="text-xs text-accent-emerald">Sta parlando</span>
          )}
    </div>
      </div>

      {/* Status indicators */}
      <div className="flex items-center gap-1">
    {participant.isMuted ? (
          <div className="p-1.5 rounded-full bg-accent-rose/10">
      <MicOff className="w-4 h-4 text-accent-rose" />
          </div>
    ) : (
          <div className={clsx(
      'p-1.5 rounded-full',
      participant.isSpeaking ? 'bg-accent-emerald/10' : 'bg-surface-tertiary'
          )}>
      <Mic className={clsx(
              'w-4 h-4',
              participant.isSpeaking ? 'text-accent-emerald' : 'text-text-quaternary'
      )} />
          </div>
    )}

    {participant.isDeafened && (
          <div className="p-1.5 rounded-full bg-surface-tertiary">
      <VolumeX className="w-4 h-4 text-text-quaternary" />
          </div>
    )}
      </div>
  </div>
  );
}
